/**
 * WHETHER A PROJECT MENU ITEM IS OPEN — the one rule, for every surface.
 *
 * The sidebar has always decided this with four gates (useProjectNav): the
 * platform-wide Features toggle, the per-project toggle, the reader's role
 * permissions, and — for Email Marketing — an active subscription. Andrew
 * (2026-09-30): a dashboard widget, a report and a push about an area exist
 * only where that area's menu item is open. "If not activated, then don't show
 * any of the widgets or even mention them."
 *
 * So the rule lives here as a pure function, and every surface asks it:
 *
 *   - the sidebar and tab bar      client/src/hooks/useProjectNav.ts
 *   - dashboard widgets            client/src/hooks/useProjectMenuGate.ts
 *   - the Reports page sections    same hook
 *   - the widget/report endpoints  server/lib/menuGate.ts (requireProjectMenu)
 *   - push notifications           server/lib/menuGate.ts (usersWithMenuOpen)
 *
 * A widget whose menu item is shut would otherwise link to a page the reader
 * cannot open, and show figures their role was never granted.
 */

import { PROJECT_MENU_ITEMS } from "./navigationMenu";

export interface MenuGateInputs {
  /** `GET /api/menu-visibility` rows. Absent row = visible. */
  globalVisibility: ReadonlyArray<{ menuKey: string; visible: boolean | null }> | null | undefined;
  /** `GET /api/projects/:id/menu-visibility`. Absent key = visible. */
  projectVisibility: Readonly<Record<string, boolean>> | null | undefined;
  /**
   * The role's `menu_permissions`, normalised. null / empty = no restriction
   * (a user with no role group sees every item — the sidebar's long-standing
   * reading, kept so this rule can never disagree with it).
   */
  rolePermissions: readonly string[] | null | undefined;
  /** `users.role`. A SUPER_ADMIN always keeps Project Settings. */
  role: string | null | undefined;
  /** The project's company holds Email Marketing (a company plan line since 2026-10-20). */
  hasEmailMarketing: boolean;
  /**
   * The project is linked to a Showroom instance (a `project_integrations`
   * row, provider `showroom`, active). Optional so a caller that predates the
   * Showroom reads as "not linked" rather than failing to compile.
   */
  hasShowroom?: boolean;
  /**
   * The project is part of a client's Agent Portal: its Company project (a
   * `project_integrations` row, provider `agent_portal`, active) or one of the
   * portal's communities (an `agent_portal_communities` row). Optional, like
   * `hasShowroom`.
   */
  hasAgentPortal?: boolean;
  /** `users.is_company_admin`: the Signing Queue is theirs on every project of the company. */
  isCompanyAdmin?: boolean;
  /** The reader is one of this project's signing officers (Project Settings → Contracts → Vendor). */
  isSigningOfficer?: boolean;
}

/** Is this project menu item open for this reader on this project? */
export function isProjectMenuOpen(inputs: MenuGateInputs, key: string): boolean {
  if (key === "emailMarketing" && !inputs.hasEmailMarketing) return false;
  if (key === "showroom" && !inputs.hasShowroom) return false;
  if (key === "agentPortal" && !inputs.hasAgentPortal) return false;
  const setting = inputs.globalVisibility?.find((s) => s.menuKey === key);
  const globallyVisible = !setting || setting.visible === true;
  const alwaysVisibleForAdmin = inputs.role === "SUPER_ADMIN" && key === "settings";
  if (!alwaysVisibleForAdmin && inputs.projectVisibility?.[key] === false) return false;
  // A SaleFish Admin works every client's Agent Portal (it acts there as the
  // SaleFish account: server/agentPortal/bridge.ts), whatever their own group
  // carries: still only on a portal's projects, and not where the project hid it.
  if (key === "agentPortal" && inputs.role === "SUPER_ADMIN") return globallyVisible;
  // The Signing Queue (Andrew, 2026-10-26): every Company Admin and every
  // signing officer, plus SaleFish Admins; nobody else, whatever their role
  // carries. Only an officer can sign there (server/routes/signingQueue.ts).
  if (key === "signingQueue") {
    const theirs = inputs.isCompanyAdmin === true || inputs.isSigningOfficer === true || inputs.role === "SUPER_ADMIN" || inputs.role === "ADMIN";
    return globallyVisible && theirs;
  }
  const perms = inputs.rolePermissions;
  const hasRolePermission = !perms || perms.length === 0 || perms.includes(key);
  return globallyVisible && hasRolePermission;
}

/** Every project menu key open for this reader, in navigation order. */
export function openProjectMenuKeys(inputs: MenuGateInputs): string[] {
  return PROJECT_MENU_ITEMS.filter((item) => isProjectMenuOpen(inputs, item.key)).map((item) => item.key);
}

/**
 * A surface gated on several areas (a lead-source-to-sale widget needs both
 * Contacts and Sales List) is open only when EVERY one is.
 */
export function allMenusOpen(open: ReadonlySet<string> | readonly string[], required: readonly string[]): boolean {
  const set = open instanceof Set ? open : new Set(open as readonly string[]);
  return required.every((k) => set.has(k));
}

/** The menu keys the sales work gates on, named once. */
export const MENU = {
  contacts: "contacts",
  tasks: "tasks",
  emailMarketing: "emailMarketing",
  websiteAnalytics: "websiteAnalytics",
  offers: "offers",
  salesList: "salesList",
  signingQueue: "signingQueue",
  salesManagement: "salesManagement",
  availability: "availability",
  reports: "reports",
  showroom: "showroom",
  agentPortal: "agentPortal",
} as const;
